import React from 'react';
import { Sparkles, Moon, Flame, Check } from 'lucide-react';
import { magicTypes } from '../data/mock';

const MagicSection = () => {
  const getGradient = (color) => {
    switch (color) {
      case 'white':
        return 'from-gray-100/20 to-white/5';
      case 'black':
        return 'from-gray-800/40 to-gray-900/20';
      case 'red':
        return 'from-red-900/30 to-red-950/10';
      default:
        return 'from-gray-800/40 to-gray-900/20';
    }
  };

  const getBorderColor = (color) => {
    switch (color) {
      case 'white':
        return 'border-gray-300/30 hover:border-gray-300/60';
      case 'black':
        return 'border-gray-600/30 hover:border-gray-500/60';
      case 'red':
        return 'border-red-800/30 hover:border-red-700/60';
      default:
        return 'border-amber-500/30';
    }
  };

  const getIconColor = (color) => {
    switch (color) {
      case 'white':
        return 'text-gray-200';
      case 'black':
        return 'text-gray-400';
      case 'red':
        return 'text-red-500';
      default:
        return 'text-amber-500';
    }
  };

  const getIcon = (color) => {
    switch (color) {
      case 'white':
        return Sparkles;
      case 'black':
        return Moon;
      case 'red':
        return Flame;
      default:
        return Sparkles;
    }
  };

  return (
    <section id="magies" className="py-16 sm:py-28 bg-black relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-amber-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-red-500/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-20">
          <span className="inline-block px-4 sm:px-5 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full text-amber-500 text-xs sm:text-sm font-semibold tracking-wide mb-4 sm:mb-6">
            Les Arts Mystiques
          </span>
          <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-8 px-2">
            Magie <span className="text-gray-200">Blanche</span>, <span className="text-gray-400">Noire</span> & <span className="text-red-500">Rouge</span>
          </h2>
          <p className="text-gray-300 text-base sm:text-xl max-w-3xl mx-auto leading-relaxed px-2">
            Le grand marabout africain maîtrise les trois formes de magie ancestrale pour répondre à chaque situation avec la technique la plus appropriée.
          </p>
        </div>

        {/* Magic Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-10">
          {magicTypes.map((magic) => {
            const IconComponent = getIcon(magic.color);
            return (
              <div
                key={magic.id}
                className={`relative bg-gradient-to-b ${getGradient(magic.color)} border ${getBorderColor(magic.color)} rounded-2xl sm:rounded-3xl p-6 sm:p-10 transition-all duration-500 hover:transform hover:-translate-y-2`}
              >
                {/* Icon Header */}
                <div className="flex items-center space-x-3 sm:space-x-5 mb-5 sm:mb-8">
                  <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl flex items-center justify-center ${magic.color === 'white' ? 'bg-white/10' : magic.color === 'red' ? 'bg-red-500/10' : 'bg-gray-700/50'}`}>
                    <IconComponent className={`w-6 h-6 sm:w-8 sm:h-8 ${getIconColor(magic.color)}`} />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-white">{magic.title}</h3>
                </div>

                {/* Description */}
                <p className="text-gray-300 text-sm sm:text-lg mb-6 sm:mb-10 leading-relaxed">
                  {magic.description}
                </p>

                {/* Benefits */}
                <ul className="space-y-3 sm:space-y-4">
                  {magic.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-center space-x-3 sm:space-x-4">
                      <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center flex-shrink-0 ${magic.color === 'white' ? 'bg-white/20' : magic.color === 'red' ? 'bg-red-500/20' : 'bg-gray-600/50'}`}>
                        <Check className={`w-3 h-3 sm:w-4 sm:h-4 ${getIconColor(magic.color)}`} />
                      </div>
                      <span className="text-gray-200 text-sm sm:text-base font-medium">{benefit}</span>
                    </li>
                  ))}
                </ul>

                {/* Decorative Elements - Hidden on mobile */}
                <div className="absolute top-4 right-4 opacity-10 sm:opacity-20 hidden sm:block">
                  <IconComponent className={`w-20 sm:w-28 h-20 sm:h-28 ${getIconColor(magic.color)}`} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom Info */}
        <div className="mt-12 sm:mt-20 text-center px-2">
          <div className="inline-flex items-center space-x-2 sm:space-x-3 px-4 sm:px-8 py-3 sm:py-4 bg-gray-900/80 border border-amber-500/20 rounded-full">
            <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500 flex-shrink-0" />
            <span className="text-gray-200 text-sm sm:text-lg font-medium">Le grand marabout choisit la technique adaptée à votre situation</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MagicSection;
